import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Botao,
  EstadoVazio,
  MenuDeAcoes,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
} from '../../componentes/ui';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import { listarColaboradores, type Colaborador } from '../../servicos/empresas';
import { simOuNao, type ColunaDaPlanilha } from '../../servicos/planilha';
import { useDesvincularColaborador } from '../../hooks/useEscrita';
import { useIdentidade } from '../../hooks/useSessao';
import { ErroDaApi } from '../ErroDaApi';
import { formatarTelefone, separarVinculos } from '../vinculos-trabalhistas';
import { CadastroDeColaborador, EdicaoDeColaborador } from './FormulariosDeColaborador';
import { SecaoDeConvites } from './Convites';

/**
 * Gestores e colaboradores da empresa.
 *
 * Cada cliente tem os seus — é o que o Deca 1 chama de "Gestores &
 * Colaboradores", e é onde se decide **quem assina** o contrato em nome da
 * empresa (RN-037) e **quem recebe** cada e-mail do cronograma, pelo vínculo
 * trabalhista (RN-025, RN-059).
 *
 * As colunas seguem o Deca 1 (`ResponsaveisLegaisCard.js`): nome, telefone,
 * papel, vínculo trabalhista, assinatura. "Vínculo trabalhista" é a coluna
 * `accounts_business.occupation` do legado — a mesma que o cronograma lê.
 *
 * Os convites de auto-cadastro moram aqui embaixo, como seção: no Deca 1 o
 * "Gerar Link" ficava dentro do drawer de colaborador, e é aqui que quem gere a
 * empresa vem procurar.
 *
 * ## A seção de convites é só de quem gerencia
 *
 * Esta aba é compartilhada pelo perfil do cliente e pelo cadastro da empresa, e
 * o perfil abre para qualquer identidade que passe em `podeVer` — colaborador
 * não-gestor da própria empresa, fornecedor com contrato ativo, assessor
 * vinculado. Gerar e listar convites, porém, exige `exigirGerenciar` na API
 * (admin ou gestor). Renderizar a seção para todo mundo fazia o colaborador
 * abrir a aba e encontrar uma caixa "Não foi possível carregar" (403) com um
 * botão "Gerar convite" que também devolvia 403.
 *
 * O token já diz quem é admin e de que empresas a pessoa é gestora, então a
 * decisão é tomada **antes** de chamar a API sempre que ele basta. O único caso
 * que o token não resolve é o gestor de outra empresa que cadastrou este
 * cliente (RN-413, `alvoEhClienteDeEmpresaGerida`): para ele a seção é
 * montada, e se a API responder 403 ela some em silêncio — ver `Convites.tsx`.
 */
/** As colunas da tabela, em valores de planilha. */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<Colaborador>[] = [
  { titulo: 'Nome', largura: 32, valor: (c) => c.fullName },
  { titulo: 'E-mail', largura: 32, valor: (c) => c.emailAddress },
  { titulo: 'Telefone', largura: 16, valor: (c) => formatarTelefone(c.phone) },
  { titulo: 'Vínculo', largura: 24, valor: (c) => c.occupation },
  { titulo: 'Papel', largura: 12, valor: (c) => c.papel },
  { titulo: 'Assina pela empresa', largura: 12, valor: (c) => simOuNao(c.assinaPelaEmpresa) },
  { titulo: 'Situação', largura: 10, valor: (c) => (c.isActive ? 'Ativo' : 'Inativo') },
];

export function AbaColaboradores(props: {
  readonly businessId: string;
  /** A razão social, para o nome da planilha extraída. */
  readonly razaoSocial?: string;
  /** `false` para o assessor: ver `CadastroDeColaborador.podeDefinirPapeis`. */
  readonly podeDefinirPapeis?: boolean;
}): JSX.Element {
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<Colaborador | null>(null);
  // Item 12 de 15/09 — "Gerar link" à vista, como no Deca 1: no topo e no
  // modal de cadastro. O contador é o pedido; a seção de convites abre o modal.
  const [pedidoDeConvite, setPedidoDeConvite] = useState(0);
  const pedirConvite = (): void => setPedidoDeConvite((n) => n + 1);
  const identidade = useIdentidade();
  const podeTentarGerenciar = podeTentarGerenciarEmpresa(identidade, props.businessId);
  const consulta = useQuery({
    queryKey: ['colaboradores', props.businessId],
    queryFn: () => listarColaboradores(props.businessId),
  });
  const desvincular = useDesvincularColaborador(props.businessId);
  const { confirmar } = useDialogo();

  const colunas: readonly ColunaTabela<Colaborador>[] = [
    {
      chave: 'fullName',
      titulo: 'Nome',
      larguraMinima: '15rem',
      celula: (c) => (
        <span className="flex flex-col">
          <span className="text-conteudo-primario">{c.fullName}</span>
          <span className="text-xs text-conteudo-sutil">{c.emailAddress}</span>
        </span>
      ),
    },
    {
      chave: 'phone',
      titulo: 'Telefone',
      larguraMinima: '9rem',
      celula: (c) =>
        c.phone ? (
          <span className="valor-fiscal whitespace-nowrap text-sm">
            {formatarTelefone(c.phone)}
          </span>
        ) : (
          <span className="text-conteudo-sutil">—</span>
        ),
    },
    {
      chave: 'papel',
      titulo: 'Papel',
      larguraMinima: '8rem',
      celula: (c) => <Selo tom={c.papel === 'Gestor' ? 'informativo' : 'neutro'}>{c.papel}</Selo>,
    },
    {
      chave: 'occupation',
      titulo: 'Vínculo trabalhista',
      larguraMinima: '12rem',
      celula: (c) => {
        const vinculos = separarVinculos(c.occupation);
        return vinculos.length === 0 ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {vinculos.map((v) => (
              <Selo key={v} tom="neutro" tamanho="pequeno">
                {v}
              </Selo>
            ))}
          </span>
        );
      },
    },
    {
      chave: 'assina',
      titulo: 'Assina pela empresa',
      larguraMinima: '11rem',
      celula: (c) =>
        c.assinaPelaEmpresa ? (
          <Selo tom="positivo">Sim</Selo>
        ) : (
          <span className="text-conteudo-sutil">Não</span>
        ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '12rem',
      // Item 7 de 15/09 — o "⋯" de toda lista. Aqui "excluir" é desvincular:
      // a pessoa é de várias empresas, e apagá-la de uma não é apagá-la.
      celula: (c) => (
        <span className="inline-flex items-center gap-2">
          <Botao variante="secundario" tamanho="pequeno" onClick={() => setEditando(c)}>
            Editar
          </Botao>
          <MenuDeAcoes
            rotulo={c.fullName}
            itens={[
              { rotulo: 'Editar', aoEscolher: () => setEditando(c) },
              {
                rotulo: 'Desvincular',
                perigo: true,
                descricao: 'Tira o acesso a esta empresa. A pessoa continua.',
                aoEscolher: () => {
                  void (async () => {
                    // Desvincular não apaga nada, mas tira o acesso na hora — e
                    // a pessoa some da lista de destinatários do cronograma.
                    const ok = await confirmar({
                      titulo: `Desvincular ${c.fullName} desta empresa?`,
                      descricao:
                        'A conta e o cadastro da pessoa continuam existindo; ela só deixa de ter acesso a esta empresa e de receber os e-mails do cronograma.',
                      rotuloConfirmar: 'Desvincular',
                      tom: 'perigo',
                    });
                    if (ok) desvincular.mutate(c.accountId);
                  })();
                },
              },
            ]}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Gestores e colaboradores
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-conteudo-secundario">
            O vínculo trabalhista importa: o cronograma escolhe destinatários por ele — quem é
            &quot;Decisor&quot; recebe aprovação, quem é &quot;Contábil&quot; ou
            &quot;Financeiro&quot; recebe cobrança.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <BotaoExtrairXlsx
            titulo={
              props.razaoSocial === undefined
                ? 'Colaboradores'
                : `Colaboradores - ${props.razaoSocial}`
            }
            disabled={consulta.data === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={consulta.data ?? []}
          />
          {podeTentarGerenciar && (
            <Botao variante="secundario" onClick={pedirConvite}>
              Gerar link de cadastro
            </Botao>
          )}
          <Botao onClick={() => setFormAberto(true)}>Cadastrar</Botao>
        </div>
      </div>

      {desvincular.isError && (
        <ErroDaApi titulo="Não foi possível desvincular" erro={desvincular.error} />
      )}
      {consulta.isError && <ErroDaApi titulo="Não foi possível carregar" erro={consulta.error} />}

      {!consulta.isLoading && (consulta.data?.length ?? 0) === 0 ? (
        <EstadoVazio
          titulo="Nenhum colaborador"
          descricao="Sem alguém cadastrado, o contrato não tem signatário e o cronograma não tem para quem enviar. Cadastre aqui ou gere um convite para a equipe se cadastrar."
          acao={<Botao onClick={() => setFormAberto(true)}>Cadastrar o primeiro</Botao>}
        />
      ) : (
        <Tabela
          legenda="Gestores e colaboradores da empresa"
          colunas={colunas}
          dados={consulta.data ?? []}
          chaveDaLinha={(c) => c.accountId}
          chaveDeCabecalhoDaLinha="fullName"
          carregando={consulta.isLoading}
        />
      )}

      {podeTentarGerenciar && (
        <SecaoDeConvites businessId={props.businessId} pedidoDeGeracao={pedidoDeConvite} />
      )}

      <CadastroDeColaborador
        businessId={props.businessId}
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(podeTentarGerenciar ? { aoGerarLink: pedirConvite } : {})}
        {...(props.podeDefinirPapeis === false ? { podeDefinirPapeis: false } : {})}
      />
      <EdicaoDeColaborador
        businessId={props.businessId}
        colaborador={editando}
        aoFechar={() => setEditando(null)}
        {...(props.podeDefinirPapeis === false ? { podeDefinirPapeis: false } : {})}
      />
    </div>
  );
}

/**
 * Se vale a pena montar a seção de convites para esta identidade.
 *
 * `true` quando o token garante a gestão (admin, ou gestor desta empresa) **ou
 * quando ele não consegue negar** — gestor de alguma outra empresa, que pode
 * ser a que cadastrou o cliente. `false` quando o token já responde que não:
 * quem não é gestor de empresa nenhuma nunca passa em `exigirGerenciar`, e
 * chamar a API só para receber o 403 seria montar a caixa de erro de propósito.
 */
function podeTentarGerenciarEmpresa(
  identidade: ReturnType<typeof useIdentidade>,
  businessId: string,
): boolean {
  if (identidade === null) return false;
  if (identidade.isAdmin) return true;

  const geridas = identidade.vinculos
    .filter((v) => v.papeis.includes('BUSINESS_OWNER'))
    .map((v) => v.businessId);

  // Gestor desta empresa: certeza. Gestor só de outras: pode ser quem
  // cadastrou o cliente — a API decide, e a seção se esconde se ela negar.
  // Sem gestão nenhuma: a API negaria com certeza, então nem se pergunta.
  if (geridas.includes(businessId)) return true;
  return geridas.length > 0;
}
